import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../components/Modal";
import { Spinner } from "../../components/Loader";
import { errorMessage } from "../../services";
import { decideReturn, getReturnPolicy, getReturns, requestUnboxingVideo, saveReturnPolicy } from "../../services/returns";
import { dateText, money } from "../../utils/format";

const STATUS_LABEL = { pending: "Waiting for you", approved: "Approved", rejected: "Rejected" };

// The store's return policy: on/off, refund and/or replacement, days after delivery, conditions shown to shoppers
function PolicyModal({ siteId, notify, onClose }) {
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    getReturnPolicy(siteId).then(({ data }) => setPolicy(data.policy)).catch((err) => { notify(errorMessage(err), "error"); onClose(); });
  }, [siteId, notify, onClose]);
  const set = (key) => (e) => setPolicy((p) => ({ ...p, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await saveReturnPolicy(siteId, { ...policy, days: Number(policy.days) });
      notify(data.message);
      onClose();
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal eyebrow="RETURNS & REFUNDS" title="Return policy" description="What your customers can ask for after delivery. Change it any time." onClose={onClose}>
      {!policy ? <div className="returns-loading"><Spinner /> Loading</div> : (
        <form onSubmit={save}>
          <div className="check-row"><label><input type="checkbox" checked={policy.enabled} onChange={set("enabled")} />Customers can ask for a return from My orders</label></div>
          {policy.enabled && (
            <>
              <div className="check-row"><label><input type="checkbox" checked={policy.replace} onChange={set("replace")} />Replacement</label></div>
              <div className="check-row"><label><input type="checkbox" checked={policy.refund} onChange={set("refund")} />Refund</label></div>
              <label className="admin-field">Days after delivery<input type="number" min="1" max="90" value={policy.days} onChange={set("days")} /></label>
              <label className="admin-field">Conditions (shown to customers)
                <textarea rows="5" maxLength={2000} value={policy.conditions} onChange={set("conditions")} placeholder="e.g. Unused items in the original box. Send an unboxing video for damaged items. Refunds within 5-7 days to the original payment." />
              </label>
            </>
          )}
          <div className="product-modal-actions">
            <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
            <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Saving</> : "Save policy"}</button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// Approve (as a refund of an amount, or a replacement) or reject one request, with a note for the customer
function DecideModal({ siteId, request, notify, onClose, onDone }) {
  const [resolution, setResolution] = useState(request.type);
  const [amount, setAmount] = useState(String(request.orderTotal || ""));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const decide = async (decision) => {
    setBusy(decision);
    try {
      const { data } = await decideReturn(siteId, request._id, { decision, resolution, refundAmount: Number(amount), note: note.trim() });
      notify(data.message);
      onDone();
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy("");
    }
  };
  return (
    <Modal eyebrow={`ORDER #${request.orderId}`} title={`${request.type === "refund" ? "Refund" : "Replacement"} request`} onClose={onClose}>
      <div className="returns-decide">
        <p className="returns-reason">“{request.reason}”</p>
        <div className="check-row returns-choice">
          <label><input type="radio" checked={resolution === "replace"} onChange={() => setResolution("replace")} />Give a replacement</label>
          <label><input type="radio" checked={resolution === "refund"} onChange={() => setResolution("refund")} />Give a refund</label>
        </div>
        {resolution === "refund" && (
          <label className="admin-field">Refund amount (₹, at most {money(request.orderTotal)})
            <input type="number" min="1" max={request.orderTotal} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
        )}
        {resolution === "refund" && <small className="field-help">Send the money yourself (UPI, or a refund from your Razorpay dashboard). A full refund marks the order Refunded.</small>}
        <label className="admin-field">Note for the customer (optional)<input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="e.g. A new pot ships tomorrow" /></label>
        <div className="product-modal-actions">
          <button type="button" className="outline-btn returns-reject" disabled={Boolean(busy)} onClick={() => decide("reject")}>{busy === "reject" ? <Spinner /> : "Reject"}</button>
          <button type="button" className="primary-btn" disabled={Boolean(busy)} onClick={() => decide("approve")}>{busy === "approve" ? <Spinner /> : resolution === "refund" ? `Approve refund` : "Approve replacement"}</button>
        </div>
      </div>
    </Modal>
  );
}

// Order management: the website's return requests, newest first, with the pending ones to answer
export default function ReturnsPanel({ siteId, notify, onChanged }) {
  const [data, setData] = useState({ requests: [], pending: 0 });
  const [open, setOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const closePolicy = useCallback(() => setPolicyOpen(false), []); // stable, so the policy loads once
  const load = useCallback(() => getReturns(siteId).then(({ data: d }) => setData(d)).catch(() => {}), [siteId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data.pending > 0) setOpen(true); }, [data.pending]);

  const askVideo = async (request) => {
    try {
      const { data: d } = await requestUnboxingVideo(siteId, request._id);
      window.open(d.link, "_blank", "noopener");
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  return (
    <section className="mo-panel returns-panel">
      <div className="returns-head">
        <button className="returns-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <b>↩ Returns &amp; refunds</b>
          {data.pending > 0 && <em className="returns-count">{data.pending} waiting</em>}
          <span aria-hidden="true">{open ? "▴" : "▾"}</span>
        </button>
        <button className="mo-secondary" onClick={() => setPolicyOpen(true)}>Return policy</button>
      </div>
      {open && (
        !data.requests.length ? <p className="returns-empty">No return requests yet. Customers ask from My orders once their order is delivered (turn it on in Return policy).</p> : (
          <ul className="returns-list">
            {data.requests.map((r) => (
              <li key={r._id} className={`is-${r.status}`}>
                <div className="returns-main">
                  <div className="returns-top">
                    <b>#{r.orderId} · {r.type === "refund" ? "Refund" : "Replacement"}</b>
                    <span className={`returns-status is-${r.status}`}>{r.status === "approved" ? (r.resolution === "refund" ? `Refund ${money(r.refundAmount)}` : "Replacement") : STATUS_LABEL[r.status]}</span>
                  </div>
                  <small>{r.customerName || "Customer"} · {r.customerPhone} · {dateText(r.createdAt)} · order {money(r.orderTotal)}</small>
                  <p>{r.reason}</p>
                  {r.photos.length > 0 && <div className="returns-photos">{r.photos.map((p) => <a key={p.url} href={p.url} target="_blank" rel="noopener noreferrer"><img src={p.url} alt="Customer's photo" /></a>)}</div>}
                  {r.photosDeletedAt && <small>Photos were deleted after 30 days.</small>}
                  {r.ownerNote && <small>Your note: {r.ownerNote}</small>}
                </div>
                {r.status === "pending" && (
                  <div className="returns-actions">
                    <button className="mo-whatsapp-btn" onClick={() => askVideo(r)}>{r.videoRequestedAt ? "Ask for video again" : "Ask for unboxing video"}</button>
                    <button className="mo-primary" onClick={() => setDeciding(r)}>Review</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )
      )}
      {policyOpen && <PolicyModal siteId={siteId} notify={notify} onClose={closePolicy} />}
      {deciding && <DecideModal siteId={siteId} request={deciding} notify={notify} onClose={() => setDeciding(null)} onDone={() => { setDeciding(null); load(); onChanged(); }} />}
    </section>
  );
}
